import ChevronIcon from '@assets/icons/chevron-up.svg?react'
import { CoreCurrencyIcon } from '@components'
import { formatCurrencyForDisplay, getUsdAccountNumber } from '@utils'
import cn from 'classnames'
import { useCurrencies } from 'mmfintech-backend-api'
import type { AccountBalanceResponse, SimpleCallback } from 'mmfintech-backend-api/types'
import { currenciesAlpha3, formatFloat, tr } from 'mmfintech-commons'
import { useMemo } from 'react'

type AccountCardProps = {
  account: AccountBalanceResponse
  active: boolean
  onClick?: SimpleCallback
}

export const AccountCard = ({ account, active, onClick }: AccountCardProps) => {
  const iban = useMemo(() => account.dedicatedMerchantBankAccounts?.[0]?.iban, [account])
  const accountNumber = getUsdAccountNumber(account)

  const { getCurrencyPrecision } = useCurrencies()

  return (
    <div
      data-test={`account-card-${account.id}`}
      className={cn('carousel-cards-wrapper', { active })}
      onClick={onClick}
    >
      <div style={{ width: '100%' }}>
        <div className='carousel-cards-header'>
          <div className='carousel-cards-header-left'>
            {account.currencyCode && <CoreCurrencyIcon currency={account.currencyCode} size='4rem' circle />}
            <div className='carousel-cards-currency-title' data-test={`account-card-currency-${account.id}`}>
              <span
                onMouseOver={event => {
                  const target = event.target as HTMLSpanElement
                  event.stopPropagation()
                  if (target.clientWidth > target.parentElement.clientWidth) {
                    target.style.transform = `translateX(${-(target.clientWidth - target.parentElement.clientWidth)}px)`
                  }
                }}
                onMouseLeave={event => {
                  const target = event.target as HTMLSpanElement
                  target.style.transform = `translateX(0px)`
                }}
              >
                {account.name ?? currenciesAlpha3[account.currencyCode]?.name}
              </span>
            </div>
          </div>
          <ChevronIcon />
        </div>

        <div className='carousel-cards-content' data-test={`account-card-balance-${account.id}`}>
          {formatFloat(
            account.availableBalance ? account.availableBalance : account.balance,
            getCurrencyPrecision(account.currencyCode)
          )}{' '}
          <span className='carousel-cards-content-currency'>{formatCurrencyForDisplay(account.currencyCode)}</span>
        </div>
      </div>

      <div className='carousel-cards-content-iban' data-test={`account-card-info-${account.id}`}>
        {iban ? (
          <>
            <div className='carousel-cards-label-wrapper'>
              <span>{tr('FRONTEND.DEPOSIT.PREVIEW.IBAN', 'IBAN')}</span>
            </div>
            <span>{iban}</span>
          </>
        ) : accountNumber ? (
          <>
            <div className='carousel-cards-label-wrapper'>
              <span>{tr('BANK_METADATA.LABELS.ACCOUNT_NUMBER', 'Account number')}</span>
            </div>
            <span>{accountNumber}</span>
          </>
        ) : (
          <>
            <div className='carousel-cards-label-wrapper'>
              <span>{tr('FRONTEND.INVOICES.SEND.TABLE.ID', 'ID')}</span>
            </div>
            <span>{account.id}</span>
          </>
        )}
      </div>
    </div>
  )
}
